import { ReactNode, useMemo } from 'react';

import {
  MetricsV1AggregationThreshold,
  MetricsV1AggregationType,
  MetricsV1ValueCap,
  getTypeOrNull,
  useFactTableMeasuresQuery,
} from '@spotify/confidence-graphql';

import { isHLLMeasurement } from '../../MeasurementSelect';
import {
  getDefaultAggregationType,
  getEnabledAggregationTypes,
} from '../aggregationUtils';
import {
  AggregatedMeasurementContext,
  AggregatedMeasurementContextValue,
} from './context';

type Measurement = { name: string } | null | undefined;

type Aggregation = {
  type: MetricsV1AggregationType;
  threshold?: MetricsV1AggregationThreshold | null;
  cap?: MetricsV1ValueCap | null;
};

export type RootProps = {
  children: ReactNode;
  measurement: Measurement;
  aggregation: Aggregation;
  entity: string;
  factTable: string;
  onChange: (measurement: Measurement, aggregation: Aggregation) => void;
};

export const Root = ({
  children,
  measurement,
  aggregation,
  entity,
  factTable,
  onChange,
}: RootProps) => {
  const { data } = useFactTableMeasuresQuery({
    skip: !factTable,
    variables: { name: factTable },
  });

  const measures = useMemo(
    () => getTypeOrNull(data?.factTable)?.measures ?? [],
    [data?.factTable],
  );
  const columnType = measures.find(m => m.column.name === measurement?.name)
    ?.column.type;
  const isHLL = isHLLMeasurement(columnType);
  const enabledAggregationTypes = getEnabledAggregationTypes(isHLL);

  const value: AggregatedMeasurementContextValue = useMemo(() => {
    const getColumnType = (name: string) =>
      measures.find(m => m.column.name === name)?.column.type;

    const updateAggregation = (updates: Partial<Aggregation>) =>
      onChange(measurement, { ...aggregation, ...updates });

    return {
      measurement,
      aggregationType: aggregation.type,
      threshold: aggregation.threshold,
      cap: aggregation.cap,
      entity,
      factTable,
      measures,
      enabledAggregationTypes,
      onMeasurementChange: (name: string) => {
        const newMeasurement = name ? { name } : undefined;
        const defaultType = getDefaultAggregationType(
          isHLLMeasurement(getColumnType(name)),
        );
        onChange(newMeasurement, { ...aggregation, type: defaultType });
      },
      onAggregationTypeChange: type => updateAggregation({ type }),
      onThresholdChange: threshold => updateAggregation({ threshold }),
      onCapChange: cap => updateAggregation({ cap }),
    };
  }, [
    measurement,
    aggregation,
    entity,
    factTable,
    measures,
    enabledAggregationTypes,
    onChange,
  ]);

  return (
    <AggregatedMeasurementContext.Provider value={value}>
      {children}
    </AggregatedMeasurementContext.Provider>
  );
};
